import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Alert,
  IconButton
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { downloadSpecialPrayerTemplate, parseFullPrayersExcel } from '../utils/ExcelPrayerService';

const BulkSpecialPrayerDialog = ({ open, onClose, categories = [], templates = [], onSuccess, API_URL }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedPrayers, setParsedPrayers] = useState([]);
  const [parsingError, setParsingError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, statusText: '' });
  const [uploadResults, setUploadResults] = useState(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setParsingError('');
    setUploadResults(null);

    try {
      const prayers = await parseFullPrayersExcel(file, categories);
      if (!prayers || prayers.length === 0) {
        setParsingError("No valid prayers found in the uploaded file.");
      } else {
        setParsedPrayers(prayers);
      }
    } catch (err) {
      console.error("Excel parse error:", err);
      setParsingError(err.message || "Failed to parse the Excel file. Please ensure it follows the template format.");
      setParsedPrayers([]);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setParsedPrayers([]);
    setParsingError('');
    setUploadResults(null);
    setUploading(false);
  };

  const handleClose = () => {
    if (uploading) return;
    handleReset();
    onClose();
  };

  const handleBulkSubmit = async () => {
    if (!parsedPrayers.length) return;

    setUploading(true);
    const token = localStorage.getItem('token');
    const defaultTemplate = templates[0] || {};
    const results = { successCount: 0, failedCount: 0, errors: [] };

    setUploadProgress({ current: 0, total: parsedPrayers.length, statusText: 'Starting upload...' });

    for (let i = 0; i < parsedPrayers.length; i++) {
      const prayer = parsedPrayers[i];
      setUploadProgress({
        current: i + 1,
        total: parsedPrayers.length,
        statusText: `Uploading (${i + 1}/${parsedPrayers.length}): "${prayer.title}"...`
      });

      try {
        const formData = new FormData();
        formData.append('title', prayer.title);
        formData.append('category', prayer.category);
        formData.append('description', prayer.description || '');
        formData.append('prayerPlan', prayer.prayerPlan);
        formData.append('fromDate', prayer.fromDate);
        formData.append('toDate', prayer.toDate);
        formData.append('numberOfDays', prayer.numberOfDays);
        formData.append('fromTime', prayer.fromTime);
        formData.append('toTime', prayer.toTime);
        formData.append('slotDuration', prayer.slotDuration);
        formData.append('prayerType', prayer.prayerType || 'Group');
        formData.append('prayerFrequency', prayer.prayerFrequency || 'Daily');
        formData.append('daysOfWeek', JSON.stringify([]));
        formData.append('selectedTemplateId', defaultTemplate?.url || null);
        formData.append('tempId', defaultTemplate?.id || 1);
        formData.append('dayWiseData', JSON.stringify(prayer.dayWiseData));

        const response = await fetch(`${API_URL}add-special-prayer`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        const data = await response.json();
        if (response.ok && data.success !== false) {
          results.successCount++;
        } else {
          results.failedCount++;
          results.errors.push(`"${prayer.title}": ${data.message || 'Server error'}`);
        }
      } catch (err) {
        results.failedCount++;
        results.errors.push(`"${prayer.title}": Network error`);
      }
    }

    setUploading(false);
    setUploadResults(results);

    if (results.successCount > 0 && onSuccess) {
      onSuccess(results.successCount);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: '#1A222C',
          color: '#E0E7EC',
          borderRadius: 3,
          border: '1px solid #2B3A4A',
          boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, borderBottom: '1px solid #2B3A4A' }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <UploadFileIcon sx={{ color: '#4E8D9E' }} />
          <Typography variant="h6" fontWeight="bold">
            Import Special Prayers from Excel
          </Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={uploading} sx={{ color: '#8E9FA9' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Step 1: Template download & file select */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="body2" color="#8E9FA9" mb={0.5}>
              Download the official template, fill in your prayer closet details, and upload it below.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={downloadSpecialPrayerTemplate}
              sx={{
                borderColor: '#4E8D9E',
                color: '#4E8D9E',
                textTransform: 'none',
                borderRadius: '8px',
                '&:hover': { borderColor: '#6FB4C6', background: 'rgba(78, 141, 158, 0.1)' }
              }}
            >
              Download Excel Template (.xlsx)
            </Button>
          </Box>

          <Button
            variant="contained"
            component="label"
            startIcon={<UploadFileIcon />}
            disabled={uploading}
            sx={{
              background: '#177373',
              color: '#FFFFFF',
              textTransform: 'none',
              borderRadius: '8px',
              px: 3,
              '&:hover': { background: '#1C8A8A' }
            }}
          >
            {selectedFile ? 'Change File' : 'Select Excel File'}
            <input type="file" hidden accept=".xlsx, .xls" onChange={handleFileChange} />
          </Button>
        </Box>

        {selectedFile && (
          <Box sx={{ mb: 2, p: 1.5, background: 'rgba(255,255,255,0.04)', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="#E0E7EC">
              Selected: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(1)} KB)
            </Typography>
            <Chip
              label={`${parsedPrayers.length} Prayers Found`}
              size="small"
              sx={{ background: '#18313D', color: '#6FB4C6', fontWeight: 600, border: '1px solid #4E8D9E' }}
            />
          </Box>
        )}

        {parsingError && (
          <Alert severity="error" sx={{ mb: 3, background: 'rgba(239, 68, 68, 0.15)', color: '#FCA5A5', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
            {parsingError}
          </Alert>
        )}

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ my: 3 }}>
            <Typography variant="body2" color="#4E8D9E" fontWeight="600" mb={1}>
              {uploadProgress.statusText}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(uploadProgress.current / uploadProgress.total) * 100}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(255,255,255,0.1)',
                '& .MuiLinearProgress-bar': { backgroundColor: '#177373' }
              }}
            />
          </Box>
        )}

        {/* Upload Results Summary */}
        {uploadResults && (
          <Box sx={{ mb: 3, p: 2, borderRadius: 2, background: uploadResults.failedCount === 0 ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {uploadResults.failedCount === 0 ? (
                <CheckCircleIcon sx={{ color: '#10B981' }} />
              ) : (
                <ErrorOutlineIcon sx={{ color: '#F59E0B' }} />
              )}
              <Typography fontWeight="bold" color={uploadResults.failedCount === 0 ? '#10B981' : '#F59E0B'}>
                Import Completed: {uploadResults.successCount} succeeded, {uploadResults.failedCount} failed.
              </Typography>
            </Box>
            {uploadResults.errors.length > 0 && (
              <Box sx={{ mt: 1, pl: 4 }}>
                {uploadResults.errors.map((err, i) => (
                  <Typography key={i} variant="caption" display="block" color="#FCA5A5">
                    • {err}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Parsed Prayers Table Preview */}
        {parsedPrayers.length > 0 && !uploadResults && (
          <TableContainer component={Paper} sx={{ maxHeight: 300, background: '#131A22', border: '1px solid #2B3A4A' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ background: '#182430', color: '#8E9FA9', fontWeight: 600 }}>Title</TableCell>
                  <TableCell sx={{ background: '#182430', color: '#8E9FA9', fontWeight: 600 }}>Plan</TableCell>
                  <TableCell sx={{ background: '#182430', color: '#8E9FA9', fontWeight: 600 }}>Dates</TableCell>
                  <TableCell sx={{ background: '#182430', color: '#8E9FA9', fontWeight: 600 }}>Days</TableCell>
                  <TableCell sx={{ background: '#182430', color: '#8E9FA9', fontWeight: 600 }}>Duration</TableCell>
                  <TableCell sx={{ background: '#182430', color: '#8E9FA9', fontWeight: 600 }}>Prayer Points</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parsedPrayers.map((prayer, i) => (
                  <TableRow key={i} sx={{ '&:hover': { background: 'rgba(255,255,255,0.03)' } }}>
                    <TableCell sx={{ color: '#E0E7EC', fontWeight: 500 }}>{prayer.title}</TableCell>
                    <TableCell sx={{ color: '#8E9FA9', textTransform: 'capitalize' }}>{prayer.prayerPlan}</TableCell>
                    <TableCell sx={{ color: '#8E9FA9', fontSize: '0.8rem' }}>
                      {prayer.fromDate || 'N/A'} ~ {prayer.toDate || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ color: '#E0E7EC' }}>{prayer.numberOfDays}</TableCell>
                    <TableCell sx={{ color: '#E0E7EC' }}>{prayer.slotDuration}m</TableCell>
                    <TableCell>
                      <Chip
                        label={`${prayer.pointsCount} points`}
                        size="small"
                        sx={{ background: '#18313D', color: '#4E8D9E', fontSize: '0.75rem' }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: '1px solid #2B3A4A' }}>
        <Button onClick={handleClose} disabled={uploading} sx={{ color: '#8E9FA9', textTransform: 'none' }}>
          {uploadResults ? 'Close' : 'Cancel'}
        </Button>
        {parsedPrayers.length > 0 && !uploadResults && (
          <Button
            variant="contained"
            onClick={handleBulkSubmit}
            disabled={uploading}
            sx={{
              background: '#177373',
              color: '#FFFFFF',
              textTransform: 'none',
              px: 4,
              borderRadius: '8px',
              fontWeight: 600,
              '&:hover': { background: '#1C8A8A' }
            }}
          >
            {uploading ? 'Importing...' : `Create ${parsedPrayers.length} Prayers`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkSpecialPrayerDialog;
